import { Descendant } from "slate";

import Role from "constants/role";
import { IDeviceListItem } from "interfaces/entities/devices";
import { ITransactionAttachment } from "interfaces/entities/transaction";
import { Theme } from "themes/helpers";

import {
  IEntityBasePayload,
  IEntityCreateFailure,
  IEntityDelete,
  IEntityDeleteSuccess,
  IEntityFetch,
  IEntityFetchCollectionSuccess,
  IEntityUpdateSuccess,
} from "../actions";

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface EmailBounced {
  date: Date;
}

export interface IAgentFlags {
  visitedWelcomeTutorial: boolean;
  verificationStepSeen?: boolean;
  mergingTicketsTooltipSeen?: boolean;
  hasSeenEnableNotificationBanner?: boolean;
  nextSubscriptionReminderDate?: string;
  inviteAgentTooltipDisplayDate?: string;
  setDomainTooltipDisplayDate?: string;
  hasSeenTryNotificationsFrameBanner?: boolean;
  hasSeenArchiveOnboardingBanner?: boolean;
  hasSeenSiloTooltip?: boolean;
  hasSeenNoResultsInSiloTooltip?: boolean;
  hasSeenEmailTemplatesPromoBanner?: boolean;
  hasSeenEmailSendTestTooltip?: boolean;
  hasSeenCustomFieldsPromoBanner?: boolean;
  hasVisitedAutomatedWorkflows?: boolean;
  hasSeenOnboardingChecklist?: boolean;
  hasSeenNavigationInTicketTooltip?: boolean;
  hasSeenEmailTemplatesBuilderPromoBanner?: boolean;
  openWidgetSnippetCollapsed?: boolean;
  hasClosedForwardingAddressesTip?: boolean;
  hasClosedReplyAddressesTip?: boolean;
  hasClosedForwardingInstructionsTooltip?: boolean;
  confirmContactFormChangesDisabled?: boolean;
  emailAddressesSectionVisitedDate?: string;
  cannedResponsesSectionVisitedDate?: string;
  tagsSectionVisitedDate?: string;
  customFieldsSectionVisitedDate?: string;
  notificationsSectionVisitedDate?: string;
  emailTemplatesSectionVisitedDate?: string;
  myViewsSectionVisitedDate?: string;
  integrationsSectionVisitedDate?: string;
  automatedWorkflowsSectionVisitedDate?: string;
  contactFormsSectionVisitedDate?: string;
  spamSectionVisitedDate?: string;
  apiSectionVisitedDate?: string;
  openWidgetSectionVisitedDate?: string;
  liveChatSectionVisitedDate?: string;
  developerConsoleVisitedDate?: string;
  archiveVisitedDate?: string;
  contactFormsPromoClosed?: boolean;
  closedMergingModalBanner?: boolean;
  preinstalledKbWidgetTooltipClosed?: boolean;
  topMessageBoxMacrosPromoBannerClosed?: boolean;
  hasClosedSimilarTicketsBanner?: boolean;
  lastClosedGoogleVerification?: string;
  hasSeenDarkModePromoTooltip?: boolean;
  handlingTimeByStatusVisitedDate?: string;
  last7DaysVisitedDate?: string;
  newTicketsVisitedDate?: string;
  newTicketsHeatmapVisitedDate?: string;
  ticketSatisfactionVisitedDate?: string;
  firstResponseTimeVisitedDate?: string;
  solvedAndClosedVisitedDate?: string;
  ticketResolutionTimeVisitedDate?: string;
  scheduledReportsVisitedDate?: string;
  auditLogVisitedDate?: string;
  wasSimilarTicketScrolledIntoView?: boolean;
  hasSeenAutoAssignmentSwitcherInProfileMenu?: boolean;
  hasClosedProfileMenuAutoAssignmentTooltip?: boolean;
  context2025PromoModalClosedDate?: string;

  // context 2024 promotional banners
  isContext2024BannerDisabled?: boolean;

  // flag added for partner program purposes, correct value will be set only for new accounts starting from 2024-10-21
  firstLoginDate?: string;

  hasSeenSortingTooltip?: boolean;
}

export enum WebpushPreferences {
  Always = "always",
  WhenOnline = "whenOnline",
  WhenOffline = "whenOffline",
}

export interface IAgentSettings {
  emailNotificationsActive: boolean;
  weeklyReports: boolean;
  timeZone: string | null;
  webpushNotificationsActive: WebpushPreferences;
  theme: Theme;
  googleAnalytics?: {
    clientID: string | null;
    sessionID: string | null;
  };
  invertColorsInDarkMode: boolean;
  canSendPrivateMsgAsViewer: boolean;
}

export interface IAgent {
  id: string;
  accountId: string;
  teamIds: string[];
  email: string;
  status: "invited" | "active";
  name: string;
  avatar: string | null;
  jobTitle: string;
  signature: {
    text: string | null;
    richTextObj: Descendant[] | null;
    attachments?: ITransactionAttachment[];
  };
  permission: Role;
  flags: IAgentFlags;
  settings: IAgentSettings;
  autoassignment: boolean;
  autoassignmentTeamIDs: string[];
  emailBounced?: EmailBounced;
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  updatedBy: string;
  emailVerified?: boolean;
  webpushSubscriptions?: IDeviceListItem[];
  organizationOwner: boolean;
}

export type IFetchSinglePayload = IEntityFetch;
export type IFetchSingleAgentSuccessPayload = { value: IAgent };
export type IFetchCollectionSuccessPayload = IEntityFetchCollectionSuccess<IAgent>;

export interface IBatchCreateAgentsPayload {
  agents: {
    email: string;
    name: string;
    permission: Role;
    teamIds: string[];
  }[];
  requestSource: string;
}

export interface IBatchCreateAgentsSuccessPayload extends IEntityBasePayload {
  agents: IAgent[];
}
export type IBatchCreateAgentsFailurePayload = IEntityCreateFailure;
export type IUpdateAgentPayload = {
  value: Omit<Partial<IAgent>, "settings"> & {
    transactionID?: string;
    settings?: Partial<IAgent["settings"]>;
  };
};

export type IUpdateAgentSuccessPayload = IEntityUpdateSuccess<IAgent>;
export type IDeleteAgentPayload = IEntityDelete;
export type ISyncDeleteAgentPayload = IEntityDeleteSuccess;

export type IFinishWelcomeTutorialPayload = Partial<IAgent>;

export type IChangeAutoAssignmentSuccessPayload = { agentId: string; autoassignment: boolean };
